import {useRef} from 'react';
import {
    Alert,
    SimpleGrid,
} from '@mantine/core';
import {useNavigate} from 'react-router-dom';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';

import {useSyntheticUsers} from '../hooks/useSyntheticUsers.ts';
import {useSyntheticUsersSearch} from '../hooks/useSyntheticUsersSearch.ts';
import {useSyntheticUsersSelection} from '../hooks/useSyntheticUsersSelection.ts';
import {useSyntheticUsersTableColumns} from '../hooks/useSyntheticUsersTableColumns.tsx';
import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions.ts';
import {rbacActions} from '../../../config/rbac/actions.ts';
import {SyntheticUser} from '../types.ts';
import config from '../config';

export const RecordsTable = () => {

    const {columns} = useSyntheticUsersTableColumns();
    const navigate = useNavigate();

    const {allowedActions} = useVerifiedPermissions();

    const {
        // The full loaded set BEFORE keyword filtering — the window the
        // footer's "N of M loaded" claim is measured against.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        nextToken,
        fetchRecords,
    } = useSyntheticUsers();

    // Same module-scoped store the list hook filters on (FNP-815) — this component
    // owns the input, useSyntheticUsers owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = useSyntheticUsersSearch();

    const {
        selection,
        handleRowSelection,
        handleClearSelection,
    } = useSyntheticUsersSelection();

    // Checkboxes appear only for an operator who can actually retire. Offering
    // selection to someone with no action to apply to it is a dead affordance.
    let selectedRecords: SyntheticUser[] = [];
    if (allowedActions.includes(rbacActions.RetireSyntheticUser)) {
        selectedRecords = visibleItems.filter(record => selection[record.id] === true);
    }

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title='Could not load synthetic users'>
                    The testing API did not return the roster. Try Reload; if it persists the
                    API may be unavailable in this environment.
                </Alert>
            )}

            <MantineDataTable<SyntheticUser>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    (items as SyntheticUser[]).forEach(item => {
                        handleRowSelection(item.id, true);
                    });
                }}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}) =>
                    navigate(`${config.basePath}/${encodeURIComponent(record.id)}`)}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'synthetic user'}
                nextToken={nextToken}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
